import { createContext, useContext } from "react";
import styled from "styled-components";
import { reportEventBus } from "./ReportEvents";
import { TINT_GREEN, SEMAPHORE } from "../tokens";


// One citation reference. `primary`/`secondary` point at either a document
// preview (pdf/image, typically a signed S3 url resolved the same way as the
// {{file:...}} chips) or a plain outbound link (BOE, a municipal visor, a
// registry portal — no preview, just "open in a new tab").
export interface CitationSource {
  label: string;
  url: string;
  kind: "pdf" | "image" | "link";
}

export interface CitationEntry {
  tag: string;
  title: string;
  detail: string;
  where?: string;
  primary?: CitationSource;
  secondary?: CitationSource;
}

export type CitationsMap = Record<string, CitationEntry>;

// The inline superscript trigger — drop this anywhere in report body text
// (own JSX, or via the {{cite:KEY}} marker parsed in BlockRenderer's
// renderWithFileRefs) to open this same shared panel.
// Calcado del diseño: no es solo un superíndice, es una pastillita con borde
// y fondo. Valores literales del template (MIKE-322).
export const CiteSup = styled.sup`
  display: inline-block;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  margin: 0 1px;
  border: 1px solid #cfe6da;
  border-radius: 5px;
  background: ${TINT_GREEN[300]};
  color: ${SEMAPHORE.green.text};
  font-weight: 800;
  font-size: 9.5px;
  line-height: 14px;
  text-align: center;
  vertical-align: super;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #d9ecdf;
  }
`;

export const openCitation = (key: string) => reportEventBus.emit("REPORT_OPEN_CITATION", key);

// The report's citation map, provided once per block tree. Body text reaches
// the superscript through a dozen nested render helpers (prose, notes, kv,
// findings...), so the map travels by context instead of being threaded as a
// parameter through every one of them.
export const CitationsContext = createContext<CitationsMap>({});

// The inline {{cite:KEY}} marker: prints the citation's short tag and opens
// the side panel. A key the report carries no entry for prints nothing: a
// marker that opens nothing is better left out than shown wrong.
export const CiteMarker = ({ cite, title }: { cite: string; title?: string }) => {
  const tag = useContext(CitationsContext)[cite]?.tag;
  if (tag == null) return null;
  return (
    <CiteSup onClick={() => openCitation(cite)} title={title}>
      {tag}
    </CiteSup>
  );
};
